import { useState } from "react";
import { Mail, Instagram, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useFetch } from "@/lib/hooks";
import { usePageSeo } from "@/lib/seo";
import { api, errMsg } from "@/lib/api";
import { Reveal } from "@/components/site/Motion";
import { PhotoImg } from "@/components/site/PhotoImg";
import { InstagramStrip } from "@/components/site/Sections";
import { PageHero } from "./Gallery";

export function About() {
  const { t, L, settings } = useLang();
  usePageSeo("about");
  const { data } = useFetch("/photos?featured=true");
  const photos = Array.isArray(data) ? data : [];
  const portrait = photos.find((p) => p.id === settings.about_photo_id) || photos[1] || photos[0];
  return (
    <div data-testid="about-page">
      <section className="mx-auto grid max-w-[1600px] gap-12 px-5 pb-24 pt-36 md:px-10 lg:grid-cols-12 lg:pt-44">
        <Reveal className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden">
            <PhotoImg photo={portrait} sizes="(max-width:1024px) 100vw, 40vw" className="h-full w-full" />
          </div>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7 lg:pt-10">
          <div className="kicker mb-5 flex items-center gap-3"><span className="h-px w-10 bg-[#5d9a35]" />{t("about")}</div>
          <h1 className="font-display text-5xl font-extrabold leading-[0.92] text-white sm:text-6xl" data-testid="about-title">{L(settings, "about_title")}</h1>
          <div className="mt-8 space-y-5 whitespace-pre-line text-white/75 md:text-lg" data-testid="about-body">{L(settings, "about_body")}</div>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href={`mailto:${settings.contact_email}`} data-testid="about-email-button" className="btn-line"><Mail size={14} /> {settings.contact_email}</a>
          </div>
        </div>
      </section>
      <InstagramStrip />
    </div>
  );
}

export function Contact() {
  const { t, settings, lang } = useLang();
  usePageSeo("contact");
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/contact", f); setDone(true); toast.success(t("sent")); }
    catch (er) { toast.error(errMsg(er)); }
    finally { setBusy(false); }
  };
  const input = "w-full border-b border-white/20 bg-transparent py-4 text-white placeholder:text-white/45 outline-none transition-colors focus:border-white";
  return (
    <div data-testid="contact-page">
      <section className="mx-auto grid max-w-[1600px] gap-16 px-5 pb-28 pt-36 md:px-10 lg:grid-cols-2 lg:pt-44">
        <div>
          <div className="kicker mb-5 flex items-center gap-3"><span className="h-px w-10 bg-[#5d9a35]" />{t("contact")}</div>
          <h1 className="font-display text-5xl font-extrabold leading-[0.92] text-white sm:text-7xl">{lang === "fr" ? <>Créons<br /><span className="font-serif-i font-normal">quelque chose</span> de durable.</> : <>Let's make<br /><span className="font-serif-i font-normal">something</span> lasting.</>}</h1>
          <p className="mt-8 max-w-md text-white/70">{t("contactLead")}</p>
          <div className="mt-10 space-y-4">
            <a href={`mailto:${settings.contact_email}`} data-testid="contact-email-link" className="link-u flex w-fit items-center gap-3 text-lg text-white"><Mail size={18} /> {settings.contact_email}</a>
            <a href={`https://www.instagram.com/${settings.instagram_handle || ""}/`} target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="link-u flex w-fit items-center gap-3 text-lg text-white"><Instagram size={18} /> @{settings.instagram_handle}</a>
          </div>
        </div>
        <Reveal>
          {done ? (
            <div className="border border-white/10 p-10" data-testid="contact-success"><p className="font-display text-3xl font-bold text-white">{t("sent")}</p></div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input required placeholder={t("name")} value={f.name} onChange={set("name")} className={input} data-testid="contact-name-input" />
              <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={input} data-testid="contact-email-input" />
              <input placeholder={t("subject")} value={f.subject} onChange={set("subject")} className={input} data-testid="contact-subject-input" />
              <textarea required rows={6} placeholder={t("message")} value={f.message} onChange={set("message")} className={`${input} resize-none`} data-testid="contact-message-input" />
              <button disabled={busy} className="btn-line mt-6" data-testid="contact-submit-button">{t("send")} <ArrowRight size={14} /></button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}

export function InstagramPage() {
  usePageSeo("instagram");
  return <div className="pt-20" data-testid="instagram-page"><InstagramStrip light={false} /></div>;
}
